<template>
  <div class="main event-editor">
    <FormWrapper ref="the_form" :action="formAction" :method="formMethod">
      <div class="title-bar">
        <h1 class="mb-0">{{ event.id ? `Edit Event (ID: ${event.id})` : `Add New Event` }}</h1>
        <a class="btn-back" :href="route('admin.events.index')">
          <span class="icon icon-arrow-left"></span>
          <span class="text">Back to List</span>
        </a>
      </div>

      <div v-if="isCancelled" class="alert alert-danger">
        <p class="m-0 text-center">
          This event has been CANCELLED for the following reason: "{{
            event.cancel_reason
          }}"
        </p>
      </div>
      <div v-else-if="isClosed" class="alert alert-warning">
        <p class="m-0 text-center">
          This event was CLOSED on
          {{ event.closed_at | moment("MM/DD/YYYY") }}
        </p>
      </div>

      <div class="row no-gutters py-4">
        <div class="col-12 col-lg-8 col-xl-9">
          <div class="row no-gutters px-3 pl-lg-4 pr-lg-0 pl-xl-5">
            <div class="col-12">
              <transition name="fade">
                <Alert v-if="hasAlert" :alert="alert"/>
              </transition>
            </div>

            <div class="col-12">
              <div class="row">
                <div class="col-6 mb-3">
                  <Field v-if="isDraft || isRequested"
                         type="select"
                         name="course_id"
                         label="Course"
                         v-model="courseId"
                         :options="courses"
                         :hide-select-empty-option="true"
                         validate="required"
                         validate-as="Course"/>
                  <Field v-else
                         type="text"
                         name="course_placer"
                         label="Course"
                         :value="event.course.name"
                         :disabled="true"
                         :readonly="true"/>
                </div>
                <div class="col-6 mb-3">
                  <Field type="text"
                         name="name"
                         label="Event Name"
                         v-model="eventName"
                         validate="required"
                         validate-as="Event Name"
                         autocomplete="do-not-autofull"/>
                </div>
                <div class="col-6 col-md-3 col-xxl-2 mb-3">
                  <Field type="datepicker"
                         name="start_date"
                         label="Start Date *"
                         v-model="startDate"/>
                </div>
                <div class="col-6 col-md-3 col-xxl-2 mb-3">
                  <Field type="datepicker"
                         name="end_date"
                         label="End Date *"
                         v-model="endDate"/>
                </div>
                <div class="col-6 col-md-3 col-xxl-2 mb-3">
                  <TimezoneField name="timezone" label="Time Zone *" :value="event.timezone"/>
                </div>
                <div class="col-6 mb-3">
                  <Field type="text"
                         name="keywords"
                         label="Keywords"
                         :value="event.keywords"/>
                </div>
                <div class="col-6 mb-3">
                  <Field type="checkbox"
                         name="use_same_time"
                         id="cb_use_same_time"
                         label="Use the same start & end time for each day of the event"
                         v-model="sameTimeEachDay"
                         :checkbox-value="1"
                         class="mb-3"/>

                  <EditEventTimes v-model="eventTimes"
                                  :start="startDate"
                                  :end="endDate"
                                  :same-time-each-day="sameTimeEachDay"/>
                </div>
                <div class="col-6 mb-3">
                  <div v-if="showCourseHours" class="bg-light py-2 px-3">
                    <p class="m-0 font-weight-bold">Course Hours:</p>
                    <p class="m-0">
                      <span v-for="(category, i) in course.categories" :key="i">
                        <br v-if="i > 0"/>
                        {{ `${category.name} - ${category.pivot.hours} hr(s)` }}
                      </span>
                    </p>
                  </div>
                </div>
                <div v-if="isRequested && !!event.alt_dates"
                     class="col-12 mb-3">
                  <div class="bg-light py-2 px-3">
                    <p class="m-0 font-weight-bold">
                      Alternate Dates & Times Suggested:
                    </p>
                    <p class="m-0" v-html="event.alt_dates"/>
                  </div>
                </div>
                <div class="col-3 mb-3">
                  <Field v-if="canEditSponsor"
                         type="select"
                         name="sponsor_id"
                         label="Sponsor *"
                         v-model="sponsorId"
                         :options="sponsorOptions"
                         selectEmptyOption="Factory Sponsored"/>
                  <Field v-else
                         type="text"
                         name="sponsor_placer"
                         label="Sponsor"
                         :value="event.sponsor_name"
                         :readonly="true"
                         :disabled="true"/>
                </div>
                <div class="col-3 mb-3">
                  <Field type="select"
                         name="contact_id"
                         label="Local Contact"
                         :value="event.contact_id"
                         :options="localContactOptions"/>
                </div>
                <div class="col-6 mb-3">
                  <label>&nbsp;</label>
                  <Field type="checkbox"
                         name="is_private"
                         id="cb_is_private"
                         label="This is a private event for the sponsoring distributor"
                         v-model="isPrivate"
                         :checkbox-value="1"/>
                </div>
              </div>
              <div class="row mt-4">
                <div class="col-lg-6 col-12">
                  <MultiAssignField name="included_courses"
                                    type="select"
                                    label="Included Courses"
                                    :options="onlineCourses"
                                    :value="includedCourses"/>
                </div>
              </div>
            </div>

            <div class="col-12">
              <hr/>
              <EditorGroupToggle label="Registration Settings"
                                 target="toggleGroupRegistration"
                                 class="mb-4"
                                 :collapsed="false"/>

              <div id="toggleGroupRegistration" class="collapse show row">
                <div class="col-3 mb-3">
                  <Field type="datepicker"
                         name="registration_opens"
                         label="Registration Open Date *"
                         :value="event.registration_opens"/>
                </div>
                <div class="col-3 mb-3">
                  <Field type="datepicker"
                         name="registration_closes"
                         label="Registration Close Date"
                         :value="event.registration_closes"/>
                </div>
                <div class="col-3 mb-3">
                  <Field type="text"
                         name="max_attendees"
                         label="Maximum Attendees"
                         v-model="maxAttendees"/>
                </div>
                <div class="col-3 mb-3">
                  <Field type="select"
                         name="form_id"
                         label="Custom Registration Form"
                         :value="event.form_id"
                         :options="forms"/>
                </div>
                <div class="col-12 mb-3">
                  <Field type="checkbox"
                         name="managers_only"
                         id="cb_managers_only"
                         label="Distributors Only - only distributor and factory employees may register"
                         v-model="managersOnly"
                         :checkbox-value="1"
                         class="mb-3"/>
                  <Field type="checkbox"
                         name="dealer_only"
                         id="cb_dealer_only"
                         label="Dealer Only - prevent distributor registration and hide distributor employee pricing"
                         v-model="dealerOnly"
                         :checkbox-value="1"
                         class="mb-3"/>
                  <Field v-if="!managersOnly"
                         type="checkbox"
                         name="fad_only"
                         id="cb_fad_only"
                         label="FAD/Elite Only - only FAD/Elite dealers may register"
                         :value="event.fad_only || false"
                         :checkbox-value="1"
                         class="mb-3"/>
                  <Field v-if="!managersOnly"
                         type="checkbox"
                         name="allow_dealer_enroll"
                         id="cb_allow_dealer_enroll"
                         label="Dealer Registration Enabled - dealers may register themselves"
                         :value="event.allow_dealer_enroll || false"
                         :checkbox-value="1"
                         class="mb-3"/>
                </div>
                <div class="col-6 mb-3">
                  <EditBrands :event="event" :course="course" :brands="brands"/>
                </div>
                <div class="col-6"></div>
              </div>
            </div>

            <EditInPersonFields v-if="isInPerson" :event="event"/>
            <EditVirtualFields v-else-if="isVirtual" :event="event"/>
            <EditFinancials :event="event" :course="course" :managers-only="managersOnly" :dealer-only="dealerOnly"/>
            <EditTrainers :event="event" :course="course" :sponsor-id="sponsorId"/>

            <div class="col-12">
              <hr/>
              <EditorGroupToggle label="Surveys & Tests"
                                 target="toggleGroupForms"
                                 class="mb-4"
                                 :collapsed="false"/>
              <div id="toggleGroupForms" class="collapse show row">
                <div class="col-3 mb-3">
                  <Field type="select"
                         label="Survey"
                         name="survey_id"
                         :options="surveys"
                         :value="surveyId"/>
                </div>
                <div class="col-3 mb-3">
                  <Field type="datepicker"
                         label="Open Date"
                         name="survey_open_date"
                         :value="event.survey_open_date"/>
                </div>
                <div class="col-3 mb-3">
                  <Field type="datepicker"
                         label="Close Date"
                         name="survey_close_date"
                         :value="event.survey_close_date"/>
                </div>
                <div class="col-3"></div>
                <div class="col-3 mb-3">
                  <Field type="select"
                         label="Test"
                         name="test_id"
                         :options="tests"
                         :value="event.test_id"/>
                </div>
                <div class="col-3 mb-3">
                  <Field type="datepicker"
                         label="Open Date"
                         name="test_open_date"
                         :value="event.test_open_date"/>
                </div>
                <div class="col-3 mb-3">
                  <Field type="datepicker"
                         label="Close Date"
                         name="test_close_date"
                         :value="event.test_close_date"/>
                </div>
                <div class="col-3"></div>
              </div>
            </div>

            <div class="col-12">
              <hr/>
              <EditorGroupToggle label="Additional Content" target="toggleGroupContent" class="mb-4"/>
              <div id="toggleGroupContent" class="collapse row">
                <div class="col-8 mb-3">
                  <h4 class="text-primary text-uppercase mb-3">Downloads</h4>
                  <Downloads :value="event._downloads" :visibilities="downloadTypes"/>
                </div>
                <div class="col-4"></div>
                <div class="col-12">
                  <hr/>
                </div>
                <div class="col-12 mb-3">
                  <HtmlField name="meta[notes]"
                             label="Additional Event Details"
                             :value="event.notes"/>
                </div>
                <div class="col-12 mb-3">
                  <HtmlField name="meta[admin_notes]"
                             label="Admin Notes"
                             :value="adminNotes"
                             :hideAssetIcons="true"/>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-4 col-xl-3 form-button-wrapper">
          <div class="sticky-top">
            <div class="row no-gutters text-center">
              <input ref="status_input"
                     type="hidden"
                     name="status"
                     :value="event.status"/>

              <div class="col-12">
                <Field type="checkbox"
                       name="disable_emails"
                       id="cb_disable_emails"
                       label="Disable notifications for this event"
                       :value="!!event.disable_emails"
                       :checkbox-value="1"/>
                <hr class="w-75"/>
              </div>

              <template v-if="isDraft">
                <div class="col-12 pb-3">
                  <button type="submit" class="btn btn-success w-50">
                    Save as Draft
                  </button>
                </div>
                <div class="col-12 pb-3">
                  <a href="#"
                     @click.prevent="openEvent()"
                     class="btn btn-warning w-50">
                    Open Event
                  </a>
                </div>
                <div class="col-12 pb-3">
                  <a :href="route('admin.events.index')" class="btn btn-info w-50">
                    Cancel & Exit
                  </a>
                </div>
              </template>

              <template v-else-if="isRequested">
                <div class="col-12 pb-3">
                  <button type="submit" class="btn btn-success w-50">
                    Approve Event
                  </button>
                </div>
                <div class="col-12 pb-3">
                  <a href="#" @click.prevent="rejectEvent()" class="btn btn-danger w-50">
                    Reject Event
                  </a>
                </div>
                <div class="col-12 pb-3">
                  <a :href="route('admin.events.index')" class="btn btn-info w-50">
                    Cancel & Exit
                  </a>
                </div>
              </template>

              <template v-else-if="isCancelled">
                <div class="col-12 pb-3">
                  <button type="submit" class="btn btn-success w-50">
                    Save Changes
                  </button>
                </div>
                <div class="col-12 pb-3">
                  <a :href="route('admin.events.index')" class="btn btn-info w-50">
                    Cancel & Exit
                  </a>
                </div>
              </template>

              <template v-else>
                <div class="col-12 pb-3">
                  <button type="submit" class="btn btn-success w-50">
                    Save Changes
                  </button>
                </div>
                <div v-if="!isCancelled" class="col-12 pb-3">
                  <a href="#" @click.prevent="cancelEvent()" class="btn btn-danger w-50">
                    Cancel Event
                  </a>
                </div>
                <div class="col-12 pb-3">
                  <a :href="route('admin.events.index')" class="btn btn-info w-50">
                    Cancel & Exit
                  </a>
                </div>
              </template>

              <template v-if="!!event.id">
                <div class="col-12">
                  <hr class="w-75"/>
                </div>
                <div class="col-12 pb-3">
                  <a :href="route('admin.events.enrollments.index', routeParam)" class="btn btn-info w-50">
                    View Roster
                  </a>
                </div>
                <div v-if="isPendingClose || isClosed" class="col-12 pb-3">
                  <a :href="route('admin.events.enrollments.attendance', routeParam)"
                     class="btn btn-info w-50">
                    Edit Attendance
                  </a>
                </div>
                <div class="col-12 pb-3">
                  <a :href="route('admin.events.surveys.index', routeParam)"
                     class="btn btn-info w-50">
                    View Surveys
                  </a>
                </div>
                <div class="col-12 font-size-sm text-center pt-4">
                  <span>
                    Last updated on
                    {{ event.updated_at | moment("MM/DD/YYYY") }}
                  </span>
                  <span>at {{ event.updated_at | moment("h:mma") }}</span>
                  <span v-if="event.updatedBy">{{ " " + event.updatedBy.name }}</span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </FormWrapper>
  </div>
</template>

<script>
import DisplaysAlerts from "mixins/DisplaysAlerts";
import EditEventTimes from "./partials/EditEventTimes";
import EditInPersonFields from "./partials/EditInPersonFields";
import EditVirtualFields from "./partials/EditVirtualFields";
import EditFinancials from "./partials/EditFinancials";
import EditBrands from "./partials/EditBrands";
import EditTrainers from "./partials/EditTrainers";
import CancelEventModal from "./partials/CancelEventModal";
import RejectEventModal from "./partials/RejectEventModal";
import MultiAssignField from "../../../forms/MultiAssignField.vue";

export default {
  mixins: [DisplaysAlerts],

  components: {
    MultiAssignField,
    EditEventTimes,
    EditInPersonFields,
    EditVirtualFields,
    EditFinancials,
    EditBrands,
    EditTrainers,
  },

  props: {
    event: {type: Object, required: true},
    types: {type: Array, default: () => []},
    courses: {type: Array, default: () => []},
    onlineCourses: {type: Array, default: () => []},
    canEditCourse: {type: Boolean, default: true},
    sponsors: {type: Array, default: () => []},
    canEditSponsor: {type: Boolean, default: true},
    contacts: {type: Array, default: () => []},
    downloadTypes: {type: Array, default: () => []},
    brands: {type: Array, default: () => []},
    forms: {type: Array, default: () => []},
    surveys: {type: Array, default: () => []},
    tests: {type: Array, default: () => []},
    cancellationTypes: {type: Array, default: () => []},
  },

  data() {
    return {
      initializing: true,
      eventType: null,
      courseId: null,
      course: null,
      eventName: "",
      startDate: null,
      endDate: null,
      sameTimeEachDay: false,
      eventTimes: [],
      sponsorId: null,
      isPrivate: false,
      managersOnly: false,
      dealerOnly: false,
      maxAttendees: null,
      adminNotes: null,
      surveyId: null,
      testId: null,
      includedCourses: null,
    };
  },

  computed: {
    routeParam() {
      return !!this.event.id ? {event: this.event.id} : null;
    },
    formAction() {
      return !this.event.id
          ? this.route("admin.events.store")
          : this.route("admin.events.update", this.routeParam);
    },
    formMethod() {
      return !this.event.id ? "POST" : "PUT";
    },
    isInPerson() {
      return !this.course || !this.course.is_virtual;
    },
    isVirtual() {
      return !!this.course && !!this.course.is_virtual;
    },
    isDraft() {
      return this.event.status == this.$root.constants.DRAFT_EVENT;
    },
    isRequested() {
      return this.event.status == this.$root.constants.REQUESTED_EVENT;
    },
    isCancelled() {
      return this.event.status == this.$root.constants.CANCELLED_EVENT;
    },
    isPendingClose() {
      return this.event.status == this.$root.constants.PENDING_CLOSE_EVENT;
    },
    isClosed() {
      return this.event.status == this.$root.constants.CLOSED_EVENT;
    },
    sponsorOptions() {
      return this.sponsors.filter((sponsor) => {
        if (!!this.course && !!this.course.is_private) {
          return sponsor.value == this.course.owned_by;
        } else {
          return true;
        }
      });
    },
    localContactOptions() {
      var contacts = this.contacts.filter((contact) => {
        if (!!this.sponsorId) {
          return contact.company_id == this.sponsorId || contact.company_les.indexOf(this.sponsorId) >= 0;
        } else {
          return !!contact.is_factory_contact;
        }
      });
      if (contacts.length == 0 && !!this.event.contact) {
        contacts.push({
          value: this.event.contact.id,
          label: this.event.contact.name,
        });
      }
      return contacts;
    },
    showCourseHours() {
      return (
          !!this.course &&
          !!this.course.categories &&
          this.course.categories.length > 0
      );
    },
  },

  watch: {
    courseId(val) {
      if (this.isDraft || this.isRequested) {
        this.loadCourse();
      }
    },
    startDate(val) {
      if (!this.initializing) {
        this.resetEndDate();
      }
    },
    endDate(val) {
      var startDate =
          typeof this.startDate == "string"
              ? new Date(this.startDate)
              : _.cloneDeep(this.startDate);
      var endDate =
          typeof this.endDate == "string"
              ? new Date(this.endDate)
              : _.cloneDeep(this.endDate);
      if (startDate > endDate) {
        alert("End Date cannot occur before Start Date");
        this.resetEndDate();
      }
    },
  },

  methods: {
    loadCourse() {
      if (!!this.courseId) {
        this.axios
            .get(this.route("admin.courses.show", {course: this.courseId}))
            .then((response) => {
              this.course = response.data;
              // populate the form with inherited values from the course
              if (
                  !this.event.course_id ||
                  this.event.course_id !== this.courseId
              ) {
                this.eventName = this.course.name;
                this.sponsorId = this.course.owned_by || "";
                this.isPrivate = !!this.course.is_private || false;
                this.managersOnly = !!this.course.managers_only || false;
                this.maxAttendees = this.course.max_attendees;
                this.adminNotes = this.course.admin_notes || null;
              }
              if (!!this.course.included_courses && (!this.event._included_courses || this.event._included_courses.length == 0)) {
                this.includedCourses = this.course.included_courses.map(course => {
                  return course.id;
                });
              }
              if (!!this.course.times && (!this.event.times || this.event.times.length == 0)) {
                this.eventTimes = this.course.times.map((time) => {
                  return {
                    start_time: time.start_time.substr(0, 5),
                    end_time: time.end_time.substr(0, 5),
                    order: parseInt(time.order, 10),
                  };
                });
              }
              if (!!this.course.survey_id && !this.surveyId) {
                this.surveyId = this.course.survey_id;
              }
              if (!!this.course.test_id && !this.testId) {
                this.testId = this.course.test_id;
              }
            });
      }
    },
    resetEndDate() {
      if (!!this.startDate) {
        var days =
            !!this.course && !!this.course.times ? this.course.times.length : 1;
        var startDate =
            typeof this.startDate == "string"
                ? new Date(this.startDate)
                : _.cloneDeep(this.startDate);
        var endDate = _.cloneDeep(startDate);
        if (days > 1) {
          endDate.setDate(endDate.getDate() + (days - 1));
        }
        this.endDate = endDate;
      }
    },
    openEvent() {
      this.$refs.status_input.value = this.$root.constants.OPEN_EVENT;
      this.$refs.the_form.submit();
    },
    cancelEvent() {
      this.$modal(CancelEventModal, {
        event: this.event,
        cancellationTypes: this.cancellationTypes,
      })
          .then(() => {
            this.goToRoute("admin.events.edit", this.routeParam);
          })
          .catch();
    },
    rejectEvent() {
      this.$modal(RejectEventModal, {
        event: this.event,
        cancellationTypes: this.cancellationTypes,
      })
          .then(() => {
            this.goToRoute("admin.events.edit", this.routeParam);
          })
          .catch();
    },
  },

  mounted() {
    this.eventType = this.event.type;
    this.courseId = this.event.course_id || null;
    this.course = this.event.course || null;
    this.eventName =
        !!this.event.course && (this.isDraft || this.isRequested)
            ? this.event.course.name
            : this.event.name || "";
    this.startDate = this.event._start_date || new Date();
    this.endDate = this.event._end_date || new Date();
    this.eventTimes = _.cloneDeep(this.event.times || []);
    this.sponsorId = this.event.sponsor_id || "";
    this.isPrivate = !!this.event.is_private || false;
    this.managersOnly = !!this.event.managers_only || false;
    this.dealerOnly = !!this.event.dealer_only || false;
    this.maxAttendees = this.event.max_attendees || null;
    this.adminNotes = this.event.admin_notes || null;
    this.surveyId = this.event.survey_id || null;
    this.testId = this.event.test_id || null;
    this.$nextTick(() => this.initializing = false)
    this.includedCourses = this.event._included_courses || null;
  },
};
</script>